import { PaginatedResponse } from '@eagle/api-types';
import { InputTypes, isPropertyInObjectFormat, Json, JsonObject, PropertiesDefinition, PropertyDefinition, ReferencePropertyDefinition, RoleFunction, TypeDefinitionTypes } from '@eagle/common';
import { ThingType } from '@eagle/core-data-types';
import { IconButton, Stack, Tooltip, Typography } from '@ethings/ui-toolkit';
import { Autocomplete, Box, TextField } from '@mui/material';
import { useDebounce } from '@react-hook/debounce';
import { isObject, isUndefined } from 'lodash';
import { DateTime } from 'luxon';
import React, { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useAuthenticated } from '../../../auth';
import { API_CALL_TEXT_LENGTH, DEFAULT_TIME_ZONE, LOOKUP_DEBOUNCE_TIME } from '../../../constants';
import { useFetchAllCache, useFetchOneCache, usePromise, useResponsiveText } from '../../../hooks';
import { CacheDataTypes, CommonEntity, Nullable } from '../../../types';
import { FILTER_OUT, innerText, testid, useHasAuthorization } from '../../../util';
import { DatePicker } from '../../date-time-range-picker/date-picker';
import { EmailEditor } from '../../email-editor';
import { ErrorMessage } from '../../error-message';
import { getMaybePropertyValue, isPropertyAuthoritative, referenceCollectionToCacheType } from '../../format';
import { Icon } from '../../icons';
import { NumberEditor } from '../../number-editor';
import { PhoneInput } from '../../phone-input';
import { SwitchEditor } from '../../switch-editor';
import { TextEditor } from '../../text-editor';
import { useStageConfirmDialogContext } from './stage-confirm-dialog-context';

const MULTILINE_INPUT_ROWS = 3;
const SYSTEM_ADMINISTRATOR_ROLE = [RoleFunction.SYSTEM_ADMINISTRATOR] as const;

const useStyles = createUseStyles({
  button: {
    border: 'none',
    borderRadius: '1rem',
    height: '1rem',
    lineHeight: 1,
    margin: 0,
    padding: 0,
    verticalAlign: 'middle',
    width: '1rem',
  },
  buttonIcon: {
    verticalAlign: 'unset',
  },
  arrayPropertyContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      width: '100%',
    },
  },
});

const isReferenceProperty = (propertyDefinition: PropertyDefinition | undefined): propertyDefinition is ReferencePropertyDefinition => {
  return !!propertyDefinition && propertyDefinition.type === TypeDefinitionTypes.REFERENCE;
};

const getDependentProperties = (property: string, propertyDefinitions: PropertiesDefinition | null): string[] => {
  const output: string[] = [];
  if (!propertyDefinitions || !propertyDefinitions.order || !propertyDefinitions.definitions) {
    return output;
  }
  for (const field of propertyDefinitions.order) {
    const definition = propertyDefinitions.definitions[field];
    if (isReferenceProperty(definition) && definition.propertyDependencies?.includes(property)) {
      output.push(field, ...getDependentProperties(field, propertyDefinitions));
    }
  }
  return [...new Set(output)];
};

const isArrayLikeProperty = (property: string, propertyDefinitions: PropertiesDefinition | null): boolean => {
  return !!propertyDefinitions?.definitions[property]?.multiple;
};

const isPropertyOfType = (property: string, type: TypeDefinitionTypes, propertyDefinitions: PropertiesDefinition | null): boolean => {
  return propertyDefinitions?.definitions[property]?.type === type;
};

const isValidJsonValue = (value: unknown): value is Json => {
  const type = typeof value;
  if (value === null || type === 'string' || type === 'number' || type === 'boolean') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every(isValidJsonValue);
  }
  if (type === 'object' && value !== null) {
    if (!value) return false;
    if (Object.prototype.toString.call(value) !== '[object Object]') return false;
    return Object.values(value).every(isValidJsonValue);
  }
  return false;
};

interface AuthoritativeButtonProps {
  disabled: boolean;
  onClick?: () => void;
  open: boolean;
}

const AuthoritativeButton: FC<AuthoritativeButtonProps> = ({
  disabled = true,
  onClick,
  open = false,
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Tooltip
      title={
        disabled
          ? t('common:component.authoritative-flag.hint.tooltip')
          : open
            ? t('common:component.authoritative-flag.unlocked.tooltip')
            : t('common:component.authoritative-flag.locked.tooltip')
      }
    >
      <IconButton disabled={disabled} onClick={onClick}>
        {disabled || !open
          ? <Icon icon="Lock" color={disabled ? 'disabled' : ''} />
          : <Icon icon="Unlock" />
        }
      </IconButton>
    </Tooltip>
  );
};

interface ReferenceEditorProps {
  definition: ReferencePropertyDefinition;
  disabled?: boolean;
  icon?: React.ReactNode;
  onChange: (value: string | null) => void;
  property: string;
  size?: 'small' | 'medium';
  sharedThingTypeId: string;
  value: string | null;
  contextProperties: JsonObject;
}

const ThingReferenceEditor: FC<ReferenceEditorProps> = ({ value, onChange, disabled, definition, icon, property, size, sharedThingTypeId, contextProperties }) => {
  const { t } = useTranslation(['common']);
  const { axios } = useAuthenticated();
  const [searchValue, setSearchValue] = useDebounce('', LOOKUP_DEBOUNCE_TIME);
  const cacheType = definition.referenceCollection ? referenceCollectionToCacheType[definition.referenceCollection] : undefined;
  const fetchOneCache = useFetchOneCache(cacheType ?? '');

  const getContextPropertyValue = useCallback((name: string): unknown => {
    const rawPropertyValue = contextProperties[name];
    return getMaybePropertyValue(rawPropertyValue);
  }, [contextProperties]);

  const [selectedEntity] = usePromise(async () => {
    if (!value || !cacheType) return null;
    try {
      return await fetchOneCache.one<CommonEntity>(value);
    } catch (error) {
      console.error(`Failed to fetch reference entity ${value} for type ${cacheType}:`, error);
      return null;
    }
  }, [value, fetchOneCache, cacheType]);

  const dependantProperties = useMemo(() => {
    const result: Record<string, { value: unknown }> = {};
    const dependencies = definition.propertyDependencies || [];
    for (const dependency of dependencies) {
      result[dependency] = { value: getContextPropertyValue(dependency) };
    }
    return result;
  }, [definition, getContextPropertyValue]);

  const hasMissingDependency = useMemo(() => Object.values(dependantProperties).some((prop) => !prop.value), [dependantProperties]);
  const stringifiedDependencies = useMemo(() => JSON.stringify(dependantProperties), [dependantProperties]);
  const isSearchValueTooShort = useMemo(() => searchValue.length > 0 && searchValue.length < API_CALL_TEXT_LENGTH, [searchValue]);

  const [options, , status] = usePromise(async () => {
    if (hasMissingDependency || isSearchValueTooShort || !property || !cacheType) return null;
    try {
      const response = await axios.get<PaginatedResponse<CommonEntity>>(`/api/v2/shared-thing-type/${sharedThingTypeId}/reference-lookup/${property}`, {
        params: {
          dependantProperties: JSON.parse(stringifiedDependencies) as Record<string, { value: unknown }>,
          filter: FILTER_OUT.deleted,
          search: searchValue || undefined,
        },
      });
      return response;
    } catch (error) {
      console.error(`Failed to fetch reference options for ${property}:`, error);
      return null;
    }
  }, [axios, sharedThingTypeId, property, hasMissingDependency, stringifiedDependencies, searchValue, isSearchValueTooShort, cacheType]);

  if (!cacheType) {
    console.error(`Invalid referenceCollection in definition for property: ${property}`);
    return <TextField label={definition.label} value="Error: Invalid configuration" disabled size={size} />;
  }

  const displayValue = (selectedEntity as CommonEntity | null)?.display ?? '';

  return (
    <Box sx={{ position: 'relative' }}>
      <Autocomplete
        disabled={disabled || hasMissingDependency}
        filterOptions={(option) => option}
        getOptionLabel={(option) => {
          const labelFromOptions = options?.data?.items?.find((opt) => opt._id === option)?.display;
          if (labelFromOptions) return labelFromOptions;
          if (option === value) return displayValue;
          return '';
        }}
        loading={status === 'pending' || isSearchValueTooShort}
        loadingText={isSearchValueTooShort ? t('common:component.search.hint.less-than-count', { count: API_CALL_TEXT_LENGTH }) : t('common:common.labels.loading')}
        onChange={(_, val) => { onChange(val || null); }}
        noOptionsText={t('common:common.hint.list.no-results')}
        onInputChange={(_, inputValue, reason) => { setSearchValue(reason === 'reset' ? '' : inputValue); }}
        options={options?.data?.items?.map((option) => option._id) ?? []}
        popupIcon={icon ? null : undefined}
        disableClearable={!!icon}
        renderInput={(inputProps) => (
          <TextField
            label={definition.label}
            {...inputProps}
            fullWidth
            sx={{ width: '100%' }}
          />
        )}
        size={size}
        sx={{
          width: '100%',
          '& .MuiAutocomplete-popupIndicator': { pointerEvents: 'auto' },
          '& .MuiAutocomplete-endAdornment': { paddingRight: '5px' },
        }}
        value={value}
      />
      {!!icon && <Box sx={{ position: 'absolute', top: '50%', right: '14px', transform: 'translate(0, -50%)', overflow: 'auto' }}>{icon}</Box>}
    </Box>
  );
};

interface ThingDetailsEditorProps {
  onValidationChange?: (isValid: boolean) => void;
  size?: 'small' | 'medium';
}

export const ThingDetailsEditor: FC<ThingDetailsEditorProps> = ({ onValidationChange, size }) => {
  const { t } = useTranslation(['common', 'admin']);
  const { state, dispatch, stage, thing } = useStageConfirmDialogContext();
  const classes = useStyles();
  const { hasAuthorization } = useHasAuthorization();
  const hasSystemAdminPermissions = hasAuthorization(SYSTEM_ADMINISTRATOR_ROLE);
  const [unlockedProperties, setUnlockedProperties] = useState<Record<string, boolean>>({});
  const thingTypeCache = useFetchAllCache(CacheDataTypes.THING_TYPE);
  const [thingType, thingTypeError, thingTypeState] = usePromise(async () => {
    if (!thing?.thingTypeId) return null;
    return thingTypeCache.one<ThingType>(thing.thingTypeId);
  }, [thing?.thingTypeId, thingTypeCache]);

  const titleText = t('admin:page.thing-detail.update-stage-dialog.thing-details.labels');
  const h6Styles = useResponsiveText('1.25rem', innerText(titleText).length, {
    minFontSize: '1rem',
    maxFontSize: '1.25rem',
    textLengthThreshold: 20,
  });

  const requiredProperties = useMemo(() => {
    if (!stage.enterConditions?.requiredProperties ||
      Object.keys(stage.enterConditions.requiredProperties).length === 0) {
      return [];
    }

    return stage.enterConditions.requiredProperties;
  }, [stage.enterConditions?.requiredProperties]);

  const missingProperties = useMemo(() => {
    if (!thing || !requiredProperties.length) return requiredProperties;

    return requiredProperties.filter((prop) => {
      const inProperties = thing.properties && prop in thing.properties;
      const inSharedProperties = thing.sharedProperties && prop in thing.sharedProperties;

      return !inProperties && !inSharedProperties;
    });
  }, [thing, requiredProperties]);

  const propertiesToDisplay = useMemo(() => {
    if (!missingProperties.length) {
      return null;
    }

    if (thingTypeState === 'pending' || !thingType) {
      return null;
    }

    const propertyDefinitions: PropertiesDefinition = {
      definitions: missingProperties.reduce<Record<string, PropertyDefinition>>((acc, prop) => {
        const thingTypePropertyDef = thingType.properties.definitions[prop] || thingType.sharedProperties?.definitions[prop];

        return {
          ...acc,
          [prop]: thingTypePropertyDef || {
            type: 'text',
            label: t(
              `admin:page.thing-detail.update-stage-dialog.${prop}.labels`,
              { defaultValue: prop.charAt(0).toUpperCase() + prop.slice(1) },
            ),
          },
        };
      }, {}),
      order: missingProperties,
    };

    return propertyDefinitions;
  }, [missingProperties, t, thingType, thingTypeState]);

  const requiredPropertiesString = useMemo(() => missingProperties.map((prop) => `${prop}:${JSON.stringify(state.requiredProperties?.[prop])}`).join('|'), [missingProperties, state.requiredProperties]);

  useEffect(() => {
    if (!missingProperties.length) {
      onValidationChange?.(true);
      return;
    }
    const hasAllRequiredValues = missingProperties.every((propertyKey) => {
      const propertyValue = state.requiredProperties?.[propertyKey];
      const value = propertyValue && typeof propertyValue === 'object' && 'value' in propertyValue
        ? propertyValue.value
        : propertyValue;

      return value !== undefined && value !== null && value !== '';
    });
    onValidationChange?.(hasAllRequiredValues);
  }, [missingProperties, onValidationChange, requiredPropertiesString, state.requiredProperties]);

  if (thingTypeError) {
    return (
      <ErrorMessage
        error={thingTypeError}
      />
    );
  }

  if (!missingProperties.length || !propertiesToDisplay) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <Typography
        variant="h6"
        style={{
          fontSize: h6Styles.fontSize,
          lineHeight: h6Styles.lineHeight,
          wordBreak: h6Styles.wordBreak,
          whiteSpace: h6Styles.whiteSpace,
          overflow: h6Styles.overflow,
          textOverflow: h6Styles.textOverflow,
        }}
      >
        {titleText}
      </Typography>
      <Stack spacing={3}>
        {propertiesToDisplay && propertiesToDisplay.order.map((property, index) => {
          const definition = propertiesToDisplay.definitions[property];
          if (!definition) {
            console.warn(`No definition found for property: ${property}`);
            return null;
          }

          const rawPropertyValue = state.requiredProperties?.[property];
          const value = getMaybePropertyValue(rawPropertyValue);

          const isUnlocked = !!unlockedProperties[property];
          const isAuthoritative = isPropertyAuthoritative(rawPropertyValue);
          const isDisabled = false || (isAuthoritative && !isUnlocked);

          const inputIcon = isAuthoritative
            ? <AuthoritativeButton
              disabled={!hasSystemAdminPermissions}
              onClick={() => setUnlockedProperties((prev) => ({ ...prev, [property]: !prev[property] }))}
              open={isUnlocked}
            />
            : undefined;

          const createChangeHandler = (propName: string) => (newValue: unknown): Promise<void> => {
            return new Promise((resolve) => {
              const dependents = getDependentProperties(propName, propertiesToDisplay);
              const update: JsonObject = {};
              const currentRawValue = state.requiredProperties?.[propName];
              const isObjectFormat = isPropertyInObjectFormat(currentRawValue);
              const baseObject = (isObjectFormat && isObject(currentRawValue)) ? currentRawValue : {};
              const finalValue = isUndefined(newValue) ? null : newValue;
              let processedValue = finalValue;
              const propDef = propertiesToDisplay?.definitions[propName];
              if (propDef?.multiple && typeof finalValue === 'string' && finalValue.trim() !== '') {
                try { processedValue = JSON.parse(finalValue); }
                catch (e) { console.error(`Invalid JSON for ${propName}:`, finalValue); processedValue = null; }
              } else if (propDef?.multiple && finalValue === '') {
                processedValue = null;
              }

              if (!isValidJsonValue(processedValue)) {
                console.error(`Invalid JSON value type for ${propName}:`, typeof processedValue);
                resolve();
                return;
              }

              update[propName] = isObjectFormat ? { ...baseObject, value: processedValue } : { value: processedValue };
              for (const dependent of dependents) {
                const depCurrentRawValue = state.requiredProperties?.[dependent];
                const depIsObjectFormat = isPropertyInObjectFormat(depCurrentRawValue);
                const depBaseObject = (depIsObjectFormat && isObject(depCurrentRawValue)) ? depCurrentRawValue : {};
                if (propertiesToDisplay?.definitions[dependent]) {
                  update[dependent] = depIsObjectFormat ? { ...depBaseObject, value: null } : { value: null };
                }
              }
              Object.entries(update).forEach(([path, val]) => {
                dispatch({ type: 'SET_REQUIRED_PROPERTY', path, value: val });
              });
              resolve();
            });
          };

          const createDateChangeHandler = (propName: string) => (newDate: Nullable<DateTime>): void => {
            void createChangeHandler(propName)(newDate ? newDate.toISO() : null);
          };

          const createReferenceChangeHandler = (propName: string) => (newValue: string | null): void => {
            void createChangeHandler(propName)(newValue);
          };

          if (isArrayLikeProperty(property, propertiesToDisplay)) {
            const arrayValue = isUndefined(value) ? '' : typeof value === 'string' ? value : JSON.stringify(value);
            return (
              <Box key={index} sx={{ width: '100%' }}>
                <div className={classes.arrayPropertyContainer}>
                  <TextEditor
                    disabled={isDisabled}
                    endAdornment={inputIcon}
                    data-testid={`property-element-${index}`}
                    value={arrayValue}
                    placeholder={isUndefined(value) ? t('common:component.properties.hint.not-set') : ''}
                    onChange={createChangeHandler(property)}
                    aria-describedby="arrayHelpBlock"
                    size={size}
                  />
                  <Typography variant='body1' id="arrayHelpBlock">
                    {isPropertyOfType(property, TypeDefinitionTypes.TEXT, propertiesToDisplay) && t('common:common.hint.array', { example: '["item_1", "item_2"]' })}
                    {isPropertyOfType(property, TypeDefinitionTypes.BOOLEAN, propertiesToDisplay) && t('common:common.hint.array', { example: '[true, false]' })}
                    {(isPropertyOfType(property, TypeDefinitionTypes.NUMBER, propertiesToDisplay) || isPropertyOfType(property, TypeDefinitionTypes.INTEGER, propertiesToDisplay)) && t('common:common.hint.array', { example: '[1, 2]' })}
                  </Typography>
                </div>
              </Box>
            );
          }

          switch (definition.type) {
            case TypeDefinitionTypes.TEXT: {
              const isMultiline = definition.input === InputTypes.MULTILINE;
              return (
                <Box
                  key={index}
                  sx={{
                    width: '100%',
                    '& > *': {
                      width: '100%',
                    },
                  }}
                >
                  <TextEditor
                    key={index}
                    data-testid={testid`${definition.label}-input`}
                    disabled={isDisabled}
                    endAdornment={inputIcon}
                    label={definition.label}
                    multiline={isMultiline}
                    onChange={createChangeHandler(property)}
                    placeholder={isUndefined(value) ? t('common:component.properties.hint.not-set') : ''}
                    required={requiredProperties.includes(property)}
                    rows={isMultiline ? MULTILINE_INPUT_ROWS : undefined}
                    size={size}
                    value={isUndefined(value) ? '' : String(value)}
                  />
                </Box>
              );
            }
            case TypeDefinitionTypes.BOOLEAN:
              return <SwitchEditor
                data-testid={testid`${definition.label}-switch`}
                key={index}
                disabled={isDisabled}
                icon={inputIcon}
                isChecked={Boolean(value)}
                label={definition.label}
                onChange={createChangeHandler(property)}
                required={requiredProperties.includes(property)}
                size={size}
              />;
            case TypeDefinitionTypes.DATE:
              return (
                <Box
                  key={index}
                  sx={{
                    width: '100%',
                    '& > *': {
                      width: '100%',
                    },
                  }}
                >
                  <DatePicker
                    data-testid={`${testid`${definition.label}`}-date-picker`}
                    date={isUndefined(value) || value === null ? null : DateTime.fromISO(String(value), { zone: DEFAULT_TIME_ZONE })}
                    disabled={isDisabled}
                    label={definition.label}
                    onChange={createDateChangeHandler(property)}
                    OpenPickerButtonProps={isAuthoritative ? { style: { pointerEvents: 'auto' } } : undefined}
                    OpenPickerIcon={isAuthoritative ? AuthoritativeButton : undefined}
                    placeholder={isUndefined(value) ? t('common:component.properties.hint.not-set') : ''}
                    required={requiredProperties.includes(property)}
                    size={size}
                  />
                </Box>
              );
            case TypeDefinitionTypes.REFERENCE:
              if (!thing?.sharedThingId) {
                console.warn('ThingReferenceEditor cannot render: missing thing.sharedThingId');
                return null;
              }
              return (
                <Box
                  key={index}
                  sx={{
                    width: '100%',
                    '& > *': {
                      width: '100%',
                    },
                  }}
                >
                  <ThingReferenceEditor
                    definition={definition as ReferencePropertyDefinition}
                    disabled={isDisabled}
                    icon={inputIcon}
                    onChange={createReferenceChangeHandler(property)}
                    property={property}
                    size={size}
                    sharedThingTypeId={thing.sharedThingId}
                    value={isUndefined(value) ? null : String(value)}
                    contextProperties={state.requiredProperties || {}}
                  />
                </Box>
              );
            case TypeDefinitionTypes.EMAIL:
              return (
                <Box
                  key={index}
                  sx={{
                    width: '100%',
                    '& > *': {
                      width: '100%',
                    },
                  }}
                >
                  <EmailEditor
                    data-testid={`${testid`${definition.label}`}-email-input`}
                    disabled={isDisabled}
                    endAdornment={inputIcon}
                    label={definition.label}
                    onChange={createChangeHandler(property)}
                    placeholder={isUndefined(value) ? t('common:component.properties.hint.not-set') : ''}
                    required={requiredProperties.includes(property)}
                    size={size}
                    value={isUndefined(value) ? '' : String(value)}
                  />
                </Box>
              );
            case TypeDefinitionTypes.NUMBER:
            case TypeDefinitionTypes.INTEGER:
              return (
                <Box
                  key={index}
                  sx={{
                    width: '100%',
                    '& > *': {
                      width: '100%',
                    },
                  }}
                >
                  <NumberEditor
                    data-testid={`${testid`${definition.label}`}-number-input`}
                    disabled={isDisabled}
                    InputProps={{
                      endAdornment: inputIcon,
                      style: { width: '100%' },
                    }}
                    label={definition.label}
                    onChange={createChangeHandler(property)}
                    placeholder={isUndefined(value) ? t('common:component.properties.hint.not-set') : ''}
                    required={requiredProperties.includes(property)}
                    size={size}
                    value={isUndefined(value) ? '' : Number(value)}
                  />
                </Box>
              );
            case TypeDefinitionTypes.PHONE:
              return (
                <Box
                  key={index}
                  sx={{
                    width: '100%',
                    '& > *': {
                      width: '100%',
                    },
                  }}
                >
                  <PhoneInput
                    data-testid={`${testid`${definition.label}`}-phone-input`}
                    disabled={isDisabled}
                    disableFormatting
                    InputProps={{
                      endAdornment: inputIcon,
                      style: { width: '100%' },
                    }}
                    label={definition.label}
                    onChange={createChangeHandler(property)}
                    size={size}
                    value={isUndefined(value) ? '' : String(value)}
                  />
                </Box>
              );
            default:
              return <span key={index}>{isUndefined(value) ? '' : String(value)}</span>;
          }
        })}
      </Stack>
    </Stack>
  );
};
